import { apiFetch } from './api';
import type { IssuedInvoiceStatus } from './issuedInvoicesApi';
import { useAuthStore } from '../store/useAuthStore';
import { putFileWithProgress, type UploadProgressHandler } from './fileUpload';

export type InvoiceDirection = 'INCOME' | 'OUTCOME';
export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';

export interface InvoiceProject {
  id: string;
  name: string;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

/** Provenance of a row: hand-entered, email ingest, Firmio-issued, or the SPV mirror. */
export type InvoiceSource = 'manual' | 'email' | 'firmio' | 'efactura' | string;

export interface Invoice {
  id: string;
  invoiceNumber: string;
  source: InvoiceSource;
  // Set on rows mirrored from ANAF SPV: the message that holds the parsed
  // document and the PDF / XML / signed archive.
  efacturaMessageId: string | null;
  // Set on rows issued from Firmio (source `firmio`): the issued invoice this
  // row mirrors, with ANAF's verdict, so Facturi can open its editor.
  issuedInvoice: {
    id: string;
    status: IssuedInvoiceStatus;
    anafMessages: string[];
    /** Set when this invoice is itself a storno. */
    stornoOfId: string | null;
    /** The storno documents issued against it (a cancelled draft included). */
    stornos: Array<{ id: string; fullNumber: string | null; status: IssuedInvoiceStatus }>;
  } | null;
  items: InvoiceItem[];
  direction: InvoiceDirection;
  status: InvoiceStatus;
  vendor: string | null;
  client: string | null;
  currency: string;
  amount: number;
  taxAmount: number;
  amountTotal: number;
  issueDate: string;
  dueDate: string | null;
  paidAt: string | null;
  notes: string | null;
  overdue: boolean;
  project: InvoiceProject | null;
  // Services nomenclature: DB-only manual tag (not part of the KB markdown).
  serviceId: string | null;
  service: { id: string; name: string; color: string | null } | null;
  // Multi-company: DB-only firm tag (NULL = default firm).
  companyId: string | null;
  company: { id: string; name: string } | null;
  fileName: string | null;
  fileType: string | null;
  s3Key: string | null;
  s3Url: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Per direction + currency totals over the whole filtered set (summary cards). */
export interface InvoiceTotal {
  direction: InvoiceDirection;
  currency: string;
  totalAmount: number;
  count: number;
}

export interface InvoiceListResponse {
  success: true;
  data: Invoice[];
  totals: InvoiceTotal[];
  meta: InvoiceListMeta;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export interface InvoiceFilters {
  type?: InvoiceDirection;
  status?: InvoiceStatus;
  projectId?: string;
  // Firm scope (multi-company): undefined = all firms.
  companyId?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
  // Free text over number / partner.
  search?: string;
  // 'true' = collected/paid, 'false' = still open.
  settled?: 'true' | 'false';
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface UploadUrlInput {
  invoiceNumber: string;
  issueDate?: string;
  projectId?: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
}

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn: number;
}

// Authorization header built from the persisted access token.
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: InvoiceFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listInvoices(filters: InvoiceFilters = {}): Promise<InvoiceListResponse> {
  return apiFetch<InvoiceListResponse>(`/api/invoices${buildQuery(filters)}`, {
    headers: authHeaders(),
  });
}

/** When the SPV scheduler last ran (per firm or overall); see useInvoiceSyncWatcher. */
export interface InvoiceSyncState {
  lastRunAt: string | null;
  lastChangeAt: string | null;
  autoSync: { enabled: boolean; intervalMinutes: number };
}

export async function getInvoiceSyncState(companyId?: string): Promise<InvoiceSyncState> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const res = await apiFetch<Envelope<InvoiceSyncState>>(`/api/invoices/sync-state${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getInvoice(id: string): Promise<Invoice> {
  const res = await apiFetch<Envelope<Invoice>>(`/api/invoices/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function requestUploadUrl(input: UploadUrlInput): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>('/api/invoices/upload-url', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// --- KB invoice edit (CLE-77) ----------------------------------------------
//
// Invoices are markdown-first (CLE-41): the per-invoice markdown file is the
// source of truth and the DB is derived from it. The read-only `/api/invoices`
// routes reject direct DB writes, so editing the type/status/project goes
// through `PATCH /api/kb/invoices/id/:id` (by id: a number alone is not unique
// — two partners, two firms), which rewrites the markdown
// FIRST (moving the file between projects when the association changes) and
// re-derives the DB row. Only these three fields are editable here; richer
// changes (amounts, dates, parties) still go through the AI assistant.

// Omitted fields are left untouched; `projectId: null` clears the association
// ("fara proiect" — company expense).
export interface UpdateKbInvoiceInput {
  direction?: InvoiceDirection;
  status?: InvoiceStatus;
  projectId?: string | null;
}

export async function updateKbInvoice(
  invoiceId: string,
  input: UpdateKbInvoiceInput,
): Promise<Invoice> {
  const res = await apiFetch<Envelope<Invoice>>(
    `/api/kb/invoices/id/${encodeURIComponent(invoiceId)}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data;
}

// Set or clear the DB-only service tag (services nomenclature). Unlike the KB
// PATCH above, this hits the dedicated /api/invoices/:id/service endpoint,
// which the KB sync engine never overwrites.
export async function updateInvoiceService(id: string, serviceId: string | null): Promise<Invoice> {
  const res = await apiFetch<Envelope<Invoice>>(`/api/invoices/${id}/service`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ serviceId }),
  });
  return res.data;
}

// Multi-company: set/clear the DB-only firm tag (NULL = default firm). Uses
// the dedicated /api/invoices/:id/company endpoint, which the KB sync engine
// never overwrites.
export async function updateInvoiceCompany(id: string, companyId: string | null): Promise<Invoice> {
  const res = await apiFetch<Envelope<Invoice>>(`/api/invoices/${id}/company`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ companyId }),
  });
  return res.data;
}

// Attach the invoice to a project (null = detach). The project's Resurse →
// Facturi list is exactly the invoices carrying its id.
export async function updateInvoiceProject(id: string, projectId: string | null): Promise<Invoice> {
  const res = await apiFetch<Envelope<Invoice>>(`/api/invoices/${id}/project`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ projectId }),
  });
  return res.data;
}

// Upload a file to the presigned S3 URL. Uses a raw fetch (not apiFetch) since
// the target is S3, not our API, and expects the raw bytes as the body.
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  await putFileWithProgress(uploadUrl, file, contentType, onProgress);
}
